import {JobCabinet} from 'components/customer/Job/store/jobApi';
import {SelectionsOverview} from 'components/customer/JobDashboard/columns/SelectionsOverview';
import {mapMaterial} from 'components/customer/Materials/helper/materialHelper';
import DrawerDetail from 'components/customer/PDF/components/job/DrawerDetail';
import DrawerEdges from 'components/customer/PDF/components/job/DrawerEdges';
import Section from 'components/customer/PDF/components/Section';
import jobDetailsConfig from 'components/customer/PDF/configs/jobDetailsConfig';
import {useProductPreviewItem} from 'components/customer/Preview3D/hooks/useProductPreviewItem';
import {getHasWebGLError} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {Product} from 'components/customer/Product/entity/Product';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {isMobile, isTablet} from 'react-device-detect';
import {useParams} from 'react-router-dom';
import {useCustomMediaQuery} from 'shared/helpers/DeviceSize';
import Icon from 'shared/Icon';
import {useAppSelector} from 'store/customer';
import styled from 'styled-components';

interface ProductInfoParams {
    getProductDataStore?: Product;
    getProductInfo?: JobCabinet;
    isRoomPreview?: boolean;
}

const ProductInfo = ({
    getProductDataStore,
    getProductInfo,
    isRoomPreview,
}: ProductInfoParams) => {
    const isSmallScreen =
        useCustomMediaQuery({maxWidth: 1024}) || isMobile || isTablet;
    const {slug} = useParams() as {slug: string};
    const [showInfo, setShowInfo] = useState(!isSmallScreen || !!isRoomPreview);
    const [showMore, setShowMore] = useState(true);
    const {setSelectedCabinetGroup} = useRoomPlannerContext();
    const hasWebGLError = useAppSelector(getHasWebGLError);

    const {
        productDataStore: hookProductDataStore,
        productInfo: hookProductInfo,
    } = useProductPreviewItem({
        slug,
        skip: isRoomPreview,
    }) as {
        productDataStore: React.MutableRefObject<Product>;
        productInfo: JobCabinet;
    };

    const productInfo = isRoomPreview ? getProductInfo : hookProductInfo;
    const productDataStore = isRoomPreview
        ? ({current: getProductDataStore} as React.MutableRefObject<Product>)
        : hookProductDataStore;

    const toggleInfo = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setShowInfo((prev) => !prev);
            // Remove focus from the button
            const button = event.target as HTMLButtonElement;
            button.blur();

            if (isRoomPreview) {
                setSelectedCabinetGroup?.(null);
            }
        },
        [isRoomPreview]
    );

    const toggleShowMore = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (isSmallScreen) {
                // small screen behavior will close the popup entirely
                setShowInfo(false);
                if (isRoomPreview) {
                    setSelectedCabinetGroup?.(null);
                }
            } else {
                setShowMore((prev) => !prev);
            }
            // Remove focus from the button
            const button = event.target as HTMLButtonElement;
            button.blur();
        },
        [isSmallScreen, isRoomPreview]
    );

    useEffect(() => {
        if (isRoomPreview && getProductDataStore?.id) {
            setShowInfo(true);
        }
    }, [isRoomPreview, getProductDataStore?.id]);

    // Without WebGL the preview is just the unavailable message, which the
    // open panel would cover. Keyed on the flag alone so the customer can
    // still reopen the panel afterwards.
    useEffect(() => {
        if (hasWebGLError && !isRoomPreview) {
            setShowInfo(false);
        }
    }, [hasWebGLError]);

    useEffect(() => {
        if (!showInfo) {
            // reset the show more state whenever we close the product info
            // for small screens ~ we will be displaying all info by default
            setShowMore(!isSmallScreen);
        }
    }, [showInfo, isSmallScreen]);

    const product = useMemo(() => {
        if (!productDataStore?.current?.id) {
            return null;
        }
        const extMaterial = mapMaterial(productDataStore.current.ext_material);
        const carcMaterial = mapMaterial(
            productDataStore.current.carc_material
        );
        return {
            ...productDataStore.current,
            door: {
                ...productDataStore.current.door,
                image: productDataStore.current.door?.image_name,
            },
            extMaterial,
            carcMaterial,
        };
    }, [productDataStore?.current?.id]);

    const [SPECS, MORE_DETAIL_FIELDS] = useMemo(() => {
        if (!productInfo) {
            return [[], []];
        }
        const filteredConfigs = jobDetailsConfig.filter(
            ({header, fields, isCustom}) => {
                const filtered =
                    fields?.filter(
                        ({field}) => productInfo[field as keyof JobCabinet]
                    ) ?? [];
                if (!isCustom && filtered.length === 0) {
                    return false;
                }
                if (['DRAWER', 'DOOR'].includes(header)) {
                    // if only style is there then hide it
                    if (
                        filtered.length === 1 &&
                        filtered[0].label === 'STYLE'
                    ) {
                        return false;
                    }
                    // Skip door and drawer sections if filler, panel, applied panel or wine rack
                    if (
                        ['Filler', 'Applied', 'Winerack', 'Panel'].some(
                            (desc) =>
                                productInfo.ProductDescription.includes(desc)
                        )
                    ) {
                        return false;
                    }
                }
                return true;
            }
        );

        const alwaysShowSections = ['SPECS', 'VARIATIONS'];

        const specsConfig = filteredConfigs.filter((config) =>
            alwaysShowSections.includes(config.header)
        );
        const moreDetailsConfig = filteredConfigs.filter(
            (config) => !alwaysShowSections.includes(config.header)
        );
        return [specsConfig, moreDetailsConfig];
    }, [productInfo]);

    if (!product) {
        return null;
    }

    return (
        <ProductInfoContainer
            $isOpen={showInfo}
            $isSmallScreen={isSmallScreen}
            $is3DRP={!!isRoomPreview}>
            {!isRoomPreview ? (
                <ProductInfoButton
                    data-cy="product-info-btn"
                    className="button-blue btn"
                    $isOpen={showInfo}
                    onClick={toggleInfo}>
                    <Icon
                        style={{height: 45, width: 45}}
                        iconName={'ProductInfo.svg'}
                    />
                    Product Info
                </ProductInfoButton>
            ) : null}

            {showInfo ? (
                <ProductDetailsWrapper $is3DRP={!!isRoomPreview}>
                    <ProductDetails $constrained={!!isRoomPreview}>
                        <ProductTitle>
                            <strong data-cy="product-name">
                                {`[${productInfo?.RoomProductNo?.replace('.00', '')}] ${productInfo?.ProductDescription}`}
                            </strong>
                            {isRoomPreview ? (
                                <CloseButton
                                    type="button"
                                    aria-label="Close"
                                    onClick={toggleInfo}>
                                    <Icon
                                        style={{height: 24, width: 24}}
                                        iconName={
                                            'Button-Cancel-Transparent.svg'
                                        }
                                    />
                                </CloseButton>
                            ) : null}
                        </ProductTitle>
                        <ScrollContainer $constrained={!!isRoomPreview}>
                            <SelectionsOverview
                                product={product}
                                isProductVisualiser
                                compact
                            />
                            <div className="d-flex flex-column">
                                {SPECS.map(({header, fields}) => {
                                    const fieldsToDisplay =
                                        fields?.filter(
                                            ({label}) => label !== 'STYLE'
                                        ) ?? [];
                                    return (
                                        <StyledSection key={header}>
                                            <Section
                                                headerText={header}
                                                fields={fieldsToDisplay}
                                                lgValue={true}
                                                hasDivider={false}
                                                gap={'5px 20px'}
                                                data={productInfo}
                                            />
                                        </StyledSection>
                                    );
                                })}
                                {MORE_DETAIL_FIELDS.map(({header, fields}) => {
                                    let fieldsToDisplay = fields;
                                    if (header === 'DRILLING') {
                                        const drillingKeys = Object.keys(
                                            productInfo
                                        ).filter((key) =>
                                            key.startsWith('Drilling')
                                        );
                                        if (drillingKeys.length) {
                                            fieldsToDisplay = drillingKeys.map(
                                                (key) => {
                                                    let label = key.replace(
                                                        'Drilling',
                                                        ''
                                                    );
                                                    const regex = /(\d+)(\D+)/;
                                                    const match =
                                                        label.match(regex);
                                                    if (match) {
                                                        const number = match[1];
                                                        const field =
                                                            match[2].trim();
                                                        label = `${field} ${number}`;
                                                    }
                                                    return {
                                                        field: key,
                                                        label: label.toUpperCase(),
                                                    };
                                                }
                                            );
                                        }
                                    }

                                    if (['DRAWER', 'DOOR'].includes(header)) {
                                        fieldsToDisplay =
                                            fields?.filter(
                                                ({field}) =>
                                                    ![
                                                        'DrawerImage',
                                                        'DoorImage',
                                                    ].includes(field)
                                            ) ?? [];
                                    }

                                    if (
                                        header === 'DRAWER DETAIL' &&
                                        productInfo?.DrawerDetail?.length
                                    ) {
                                        return (
                                            <StyledSection
                                                key={header}
                                                $isHidden={showMore}>
                                                <div className="inner">
                                                    <DrawerDetail
                                                        key={header}
                                                        drawers={
                                                            productInfo.DrawerDetail
                                                        }
                                                    />
                                                </div>
                                            </StyledSection>
                                        );
                                    }

                                    if (
                                        header === 'DRAWER EDGING' &&
                                        productInfo?.DrawerEdging?.length
                                    ) {
                                        return (
                                            <StyledSection
                                                key={header}
                                                $isHidden={showMore}>
                                                <div className="inner">
                                                    <DrawerEdges
                                                        key={header}
                                                        drawerEdges={
                                                            productInfo.DrawerEdging
                                                        }
                                                    />
                                                </div>
                                            </StyledSection>
                                        );
                                    }

                                    return (
                                        <StyledSection
                                            key={header}
                                            $isHidden={showMore}>
                                            <div className="inner">
                                                <Section
                                                    headerText={header}
                                                    fields={fieldsToDisplay}
                                                    lgValue={false}
                                                    hasDivider={true}
                                                    gap={'5px 14px'}
                                                    data={productInfo}
                                                />
                                            </div>
                                        </StyledSection>
                                    );
                                })}
                            </div>
                        </ScrollContainer>
                        <section className="d-flex flex-column">
                            <div className="hr" style={{borderWidth: 2}} />
                            {isSmallScreen ? (
                                <MoreButton
                                    data-cy="close-button"
                                    className="button-light btn"
                                    style={{width: 86}}
                                    onClick={toggleShowMore}>
                                    <span>Close</span>
                                    <Icon
                                        key="button-close"
                                        style={{
                                            height: 20,
                                            width: 20,
                                            margin: 0,
                                        }}
                                        iconName={'Button-Cancel.svg'}
                                    />
                                </MoreButton>
                            ) : (
                                <MoreButton
                                    data-cy="details-button"
                                    className="button-blue btn"
                                    onClick={toggleShowMore}>
                                    {showMore ? (
                                        <>
                                            More details
                                            <Icon
                                                key="button-down"
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    margin: '0 0 0 7px',
                                                }}
                                                iconName={'Button-Down.svg'}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            Less details
                                            <Icon
                                                key="button-up"
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    margin: '0 0 0 12px',
                                                }}
                                                iconName={'Button-Up.svg'}
                                            />
                                        </>
                                    )}
                                </MoreButton>
                            )}
                        </section>
                    </ProductDetails>
                </ProductDetailsWrapper>
            ) : null}
        </ProductInfoContainer>
    );
};

export const ProductInfoContainer = styled.div<{
    $isOpen: boolean;
    $isSmallScreen: boolean;
    $is3DRP: boolean;
}>`
    position: ${({$is3DRP}) => ($is3DRP ? 'absolute' : 'relative')};
    ${({$is3DRP, $isSmallScreen, $isOpen}) =>
        $is3DRP
            ? `
        inset: 0;
        width: auto;
        overflow: hidden;
        pointer-events: ${$isSmallScreen && $isOpen ? 'auto' : 'none'};
    `
            : `width: 100vw;`}
    z-index: 10000;
    transition: background-color 0.3s ease;
    background-color: ${({$isOpen, $isSmallScreen}) =>
        $isSmallScreen && $isOpen ? 'rgba(0, 0, 0, 0.5)' : 'unset'};
    height: ${({$isOpen, $isSmallScreen, $is3DRP}) =>
        $is3DRP ? 'auto' : $isSmallScreen && $isOpen ? '100%' : 'unset'};
`;

const ProductInfoButton = styled.button<{$isOpen: boolean}>`
    position: absolute;
    right: 100px;
    top: 30px;
    font-size: 0.9rem;
    font-weight: normal;
    width: 135px;
    text-align: left;
    line-height: 1.1;
    padding: 4px;
    transition: border-radius 0.1s ease-in-out;
    ${({$isOpen}) =>
        $isOpen
            ? `
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    `
            : ''}
    @media screen and (max-width: 768px) and (orientation: portrait) {
        top: 85px;
        right: 15px;
    }
    &:hover {
        background-color: ${({theme}) =>
            theme.colors.secondary?.main} !important;
    }
`;

const MoreButton = styled.button`
    font-size: 0.8rem;
    font-weight: normal;
    height: 26px;
    width: 122px;
    line-height: 1.1;
    padding: 2px 2px 2px 12px;
    align-self: end;
    &:hover {
        background-color: ${({theme}) =>
            theme.colors.secondary?.main} !important;
    }
`;

export const ProductTitle = styled.div`
    display: flex;
    align-items: flex-start;
    flex-shrink: 0;
    gap: 12px;

    > strong {
        flex: 1 1 auto;
        min-width: 0;
        color: ${({theme}) => theme.colors.secondary?.main};
        text-transform: uppercase;
        overflow-wrap: break-word;
    }
`;

export const CloseButton = styled.button`
    flex-shrink: 0;
    border: 0;
    padding: 0;
    background: transparent;
    line-height: 0;
    cursor: pointer;
`;

export const ProductDetails = styled.div<{$constrained?: boolean}>`
    margin: 4px;
    padding: 1rem;
    border-radius: 18px;
    background: white;
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    ${({$constrained}) =>
        $constrained
            ? `
        flex: 1 1 auto;
        overflow: hidden;

        > section {
            flex-shrink: 0;
        }
    `
            : ''}
    & > strong {
        color: ${({theme}) => theme.colors.secondary?.main};
        text-transform: uppercase;
    }
    .hr {
        height: 1px;
        margin: 3px 0;
        border-bottom: 1px solid ${({theme}) => theme.colors.secondary?.main};
    }
`;

export const ProductDetailsWrapper = styled.div<{$is3DRP: boolean}>`
    position: absolute;
    border-radius: 20px;
    ${({$is3DRP}) =>
        $is3DRP
            ? `
        left: 10px;
        top: 48px;
        max-height: calc(100% - 56px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        pointer-events: auto;
    `
            : `right: 100px; top: 84px; border-top-right-radius: 0;`}
    width: 500px;
    background: ${({theme}) => theme.colors.secondary?.main};
    transition: opacity 0.2s;
    opacity: 1;

    @starting-style {
        opacity: 0;
    }

    @media screen and (max-width: 768px) and (orientation: portrait) {
        ${({$is3DRP}) =>
            $is3DRP
                ? `
            top: 48px;
            left: 10px;
            right: 10px;
            width: auto;
            max-height: calc(100% - 56px);
        `
                : `
            top: 139px;
            right: 15px;
            width: calc(100vw - 8%);
        `}
    }
`;

export const StyledSection = styled.div<{$isHidden?: boolean}>`
    .flex {
        display: flex;
    }
    .flex-1 {
        flex: 1;
    }
    .flex-col {
        display: flex;
        flex-direction: column;
    }
    .mt-5 {
        margin-top: 0 !important;
    }
    .grey {
        color: #918f8f;
    }
    .red {
        color: red;
    }
    .secondary {
        color: ${({theme}) => theme.colors.secondary?.main};
    }
    .font-xs-2 {
        font-size: 11px !important;
    }
    .font-sm {
        font-size: 12px !important;
    }
    strong {
        font-size: 12px !important;
    }
    display: grid;
    transition:
        grid-template-rows 0.3s ease-out,
        visibility 0.3s;
    visibility: ${({$isHidden}) => ($isHidden ? 'hidden' : 'visible')};
    grid-template-rows: ${({$isHidden}) => ($isHidden ? '0fr' : '1fr')};
    & > .inner {
        overflow: hidden;
    }
    section > div > strong {
        color: ${({theme}) => theme.colors.secondary?.main};
    }
`;

export const ScrollContainer = styled.div<{$constrained?: boolean}>`
    overflow: auto;
    max-height: ${({$constrained}) =>
        $constrained ? 'none' : 'calc(100vh - 230px)'};
    flex: ${({$constrained}) => ($constrained ? '1 1 auto' : '0 1 auto')};
    min-height: ${({$constrained}) => ($constrained ? 0 : 'auto')};
    display: flex;
    flex-direction: column;
    @media screen and (max-width: 768px) and (orientation: portrait) {
        max-height: ${({$constrained}) =>
            $constrained ? 'none' : 'calc(100vh - 400px)'};
    }
`;

export default ProductInfo;
